import { CSSProperties, useEffect, useState } from 'react';
import * as R from 'ramda';

import { usePlacesAutocomplete } from '@atom/map';
import { Autocomplete, Icon } from '@atom/mui';
import { LocationOnIcon } from '@atom/mui/icons';
import { GoogleMapsLocation } from '@atom/types/gmapsLocation';

const styles = {
  leftIconMarker: {
    minWidth: '20px',
    maxWidth: '20px',
    paddingRight: '5px',
  },
  menuItem: {
    fontSize: 13,
    overflow: 'hidden',
    paddingRight: '1rem',
    paddingLeft: '1rem',
  },
};

type GooglePlacesAutoCompleteProps = {
  initialValue?: string;
  hintText?: string;
  textFieldStyle?: CSSProperties;
  onSelect?: (place: GoogleMapsLocation, searchTerm?: string) => void;
  anchor?: Element;
  popoverWidth?: string | number;
};

const GooglePlacesAutoComplete = ({
  initialValue,
  hintText,
  textFieldStyle,
  onSelect,
  anchor,
  popoverWidth,
}: GooglePlacesAutoCompleteProps) => {
  const {
    ready,
    suggestions,
    fetchSuggestions,
    clearSuggestions,
    resolveSelection,
  } = usePlacesAutocomplete();

  const [searchText, setSearchText] = useState<string>(initialValue || '');

  useEffect(() => {
    setSearchText(initialValue || '');
  }, [initialValue]);

  useEffect(() => {
    if (!ready || R.isEmpty(searchText)) {
      clearSuggestions();
      return;
    }

    fetchSuggestions(searchText);
  }, [searchText, ready]);

  const onNewRequest = async (_event, newValue) => {
    const resolution = await resolveSelection(searchText, newValue);

    if (!resolution) {
      return;
    }

    onSelect(resolution.location, resolution.formattedAddress);

    if (!resolution.isCoordinate) {
      setSearchText(resolution.formattedAddress || '');
    }
  };

  const renderOption = (
    props,
    option: google.maps.places.AutocompleteSuggestion,
  ) => {
    const { key, ...optionProps } = props;

    return (
      <li key={key} style={styles.menuItem} {...optionProps}>
        <Icon style={styles.leftIconMarker} icon={LocationOnIcon} />
        <span>{option?.placePrediction?.text?.text}</span>
      </li>
    );
  };

  return (
    <Autocomplete
      options={suggestions}
      disableClearable
      filterOptions={options => options}
      getOptionLabel={option =>
        typeof option === 'string'
          ? option
          : option.placePrediction?.text?.text || ''
      }
      renderOption={renderOption}
      inputValue={searchText}
      onInputChange={(_event, value, reason) => {
        if (reason !== 'reset') {
          setSearchText(value || '');
        }
      }}
      onChange={onNewRequest}
      placeholder={hintText || ''}
      disableUnderline
      popperAnchorEl={anchor}
      popperWidth={popoverWidth}
      TextFieldProps={{ style: { paddingTop: '0.25rem', ...textFieldStyle } }}
      sx={{ width: '100%' }}
    />
  );
};

export default GooglePlacesAutoComplete;
